import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AOS from 'aos';
import 'aos/dist/aos.css'; // Import AOS styles

const SchoolERP = () => {
  const [faqOpen, setFaqOpen] = useState([true, false, false]);
  const [visibleItems, setVisibleItems] = useState([]);
  const itemRefs = useRef([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+91',
    phoneNumber: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formMessage, setFormMessage] = useState('');
  const videoRef = useRef(null);

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);

    // Initialize WOW.js for animations
    if (window.WOW) {
      new window.WOW().init();
    }

    // Initialize AOS
    AOS.init({
      duration: 1000,
      once: true,
      easing: 'ease-in-out',
    });

    // Google Analytics
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    gtag('js', new Date());
    gtag('config', 'G-P2TS4436R5');

    // Intersection Observer for animated items
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleItems((prev) => [...prev, entry.target.dataset.index]);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    itemRefs.current.forEach((item) => {
      if (item) observer.observe(item);
    });

    // Parallax effect for video
    const handleScroll = () => {
      if (videoRef.current) {
        const scrollPosition = window.scrollY;
        const videoHeight = videoRef.current.offsetHeight;
        const translateY = scrollPosition * 0.3;
        videoRef.current.style.transform = `translateY(${translateY}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      itemRefs.current.forEach((item) => {
        if (item) observer.unobserve(item);
      });
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleFaq = (index) => {
    setFaqOpen((prev) => prev.map((isOpen, i) => (i === index ? !isOpen : false)));
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      setTimeout(() => {
        setFormMessage('✅ Message Sent Successfully!');
        setFormData({
          name: '',
          email: '',
          countryCode: '+91',
          phoneNumber: '',
          subject: '',
          message: '',
        });
        setIsSubmitting(false);
      }, 1000);
    } catch (error) {
      setFormMessage('❌ Failed to send message. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <Navbar />
      {/* Hero Section with Parallax Video */}
      <div className="relative h-[40vh] min-h-[300px] xs:h-[45vh] sm:h-[50vh] md:h-[60vh] lg:h-[70vh] xl:h-[80vh] 2xl:h-[85vh] overflow-hidden" data-aos="fade">
        <video
          ref={videoRef}
          className="absolute top-0 left-0 w-full h-[130%] object-cover"
          autoPlay
          loop
          muted
          playsInline
        >
          <source src="/video/SAMS.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="absolute inset-0 bg-black opacity-50"></div>
<div className="relative z-10 w-full h-full flex flex-col justify-between">
          <div className="text-left py-4 px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-20">
            <h1
              className="text-xl xs:text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white wow animate__zoomIn"
              data-wow-delay="0.1s"
            >
              Our Products
            </h1>
          </div>
          <div
            className="bg-white mx-auto"
            style={{
              width: "80%",
              height: "1px",
              maxWidth: "1200px",
            }}
          ></div>
          <div className="absolute inset-0 flex items-center justify-center px-4 sm:px-6">
            <h1
              className="text-lg xs:text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-extrabold text-white wow animate__zoomIn text-center"
              data-wow-delay="0.3s"
            >
             Intelligent School Admin Software
            </h1>
          </div>
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="container mx-auto text-center px-4">
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <section className="bg-white rounded-2xl shadow-xl p-10 mb-16 border-t-4 border-blue-600" data-aos="fade-up">
          <h3 className="text-2xl font-semibold text-blue-600 mb-6">Connect Your Entire School - In Real Time</h3>
          <p className="text-lg text-gray-700 leading-relaxed animate-fadeIn">
            SecurXperts’ School Administration Management Software is a secure, cloud-based ERP that connects administrators, teachers, parents and students in real time. Parents can access reports, fees, leave requests, and updates online without needing to visit the school. From admissions to transfer certificates, every process is digital, saving time, reducing errors and boosting efficiency.
          </p>
        </section>

        <section className="mb-16" data-aos="fade-up">
          <h2 className="text-3xl font-bold text-blue-600 text-center mb-12">Key Features</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: 'Student Attendance Management',
                description: 'Digitally track student attendance and instantly notify parents, ensuring precise and transparent records.',
                image: '/img/student attendence management.png',
              },
              {
                title: 'Teacher & Classroom Management',
                description: 'Teachers can schedule classes, assign lessons, share homework and track performance—all in one simple platform.',
                image: '/img/teacher and class room.png',
              },
              {
                title: 'Transport Management',
                description: 'GPS-enabled bus tracking ensures safe travel, with real-time route updates and alerts for parents and school admins.',
                image: '/img/transport management.png',
              },
              {
                title: 'Accounts & Payroll Management',
                description: 'Automate fee collection, payroll, and expenses with secure online payments, reminders and audit-ready reports.',
                image: '/img/Accounts & Payroll Management.png',
              },
              {
                title: 'Student Progress & Reports',
                description: 'Plan exams, generate digital report cards, and analyse performance trends to identify and address learning gaps.',
                image: '/img/students progress.png',
              },
              {
                title: 'Library Management',
                description: 'Easily manage book issues, returns, and fines with barcode/RFID systems, plus provide access to digital resources.',
                image: '/img/library.png',
              },
            ].map((feature, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl p-6 shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 ${
                  visibleItems.includes(String(index)) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                data-index={index}
                ref={(el) => (itemRefs.current[index] = el)}
                data-aos="zoom-in"
                data-aos-delay={index * 100}
              >
                <img
                  src={feature.image}
                  alt={feature.title}
                  className="w-full max-w-[404px] h-[269px] object-cover rounded-lg mb-3 sm:mb-4 mx-auto"
                />
                <i className={`${feature.icon} text-blue-600 text-3xl mb-4`}></i>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-700">{feature.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8" data-aos="fade-up" data-aos-delay="100">
            <Link
              to="/contact-us"
              className="inline-block bg-blue-600 text-white font-medium py-3 px-8 rounded-lg hover:bg-blue-700 transition duration-300 shadow-md no-underline hover:scale-105"
            >
              Talk to an Expert
            </Link>
          </div>
        </section>

        <section
          className="mb-16 bg-fixed bg-cover bg-center"
          style={{ backgroundImage: "url('/img/school-bg.png')" }}
          data-aos="fade-up"
        >
          <div className="bg-white bg-opacity-90 rounded-2xl p-6 sm:p-8">
            <h2 className="text-3xl font-bold text-blue-600 text-center mb-12">Why Choose Us</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6">
              {[
                {
                  icon: 'fas fa-desktop',
                  title: 'Unified Dashboard',
                  description: 'All school operations - academics, fees, transport, library, and exams—are managed through a simple, connected dashboard.',
                },
                {
                  icon: 'fas fa-shield-alt',
                  title: 'Enterprise-Grade Security',
                  description: 'Keep data safe with 256-bit encryption, role-based access and automatic backups built into the system.',
                },
                {
                  icon: 'fas fa-mobile-alt',
                  title: 'Anytime, Anywhere Access',
                  description: 'Teachers, parents, and students can log in securely from any device, making school management truly mobile.',
                },
                {
                  icon: 'fas fa-cogs',
                  title: 'Custom Modules',
                  description: 'From attendance tracking to bus route monitoring, choose and configure modules that match your school’s unique needs.',
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className={`bg-white rounded-2xl p-6 shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 ${
                    visibleItems.includes(String(index + 6)) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                  data-index={index + 6}
                  ref={(el) => (itemRefs.current[index + 6] = el)}
                  data-aos="zoom-in"
                  data-aos-delay={index * 100}
                >
                  <i className={`${item.icon} text-blue-600 text-3xl mb-4`}></i>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-700">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-16" data-aos="fade-up">
          <h2 className="text-3xl font-bold text-blue-600 text-center mb-12">Who Can Use SecurXperts' School ERP</h2>
          <p className="text-center text-gray-700 mb-10 leading-relaxed animate-fadeIn">
            Our platform fits all types of educational institutions:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: 'fas fa-school',
                title: 'Schools',
                description: '',
              },
              {
                icon: 'fas fa-university',
                title: 'Colleges',
                description: '',
              },
              {
                icon: 'fas fa-chalkboard-teacher',
                title: 'Coaching Centres',
                description: '',
              },
              {
                icon: 'fas fa-graduation-cap',
                title: 'Universities',
                description: '',
              },
              {
                icon: 'fas fa-book-open',
                title: 'Training Institutes',
                description: '',
              },
            ].map((useCase, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl p-6 shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 ${
                  visibleItems.includes(String(index + 10)) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                data-index={index + 10}
                ref={(el) => (itemRefs.current[index + 10] = el)}
                data-aos="zoom-in"
                data-aos-delay={index * 100}
              >
                <i className={`${useCase.icon} text-blue-600 text-3xl mb-4`}></i>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{useCase.title}</h3>
                <p className="text-gray-700">{useCase.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-blue-600 text-white rounded-2xl shadow-xl p-10 mb-16 text-center" data-aos="fade-up">
          <h4 className="text-2xl font-bold mb-6">Get Started Today</h4>
          <p className="text-lg mb-8 max-w-3xl mx-auto animate-fadeIn">
            Make your school smarter and more connected.
          </p>
          <div className="flex justify-center gap-4" data-aos="fade-up" data-aos-delay="100">
            <Link
              to="/contact-us"
              className="inline-block bg-white text-blue-600 font-medium py-3 px-8 rounded-lg hover:bg-gray-100 transition duration-300 shadow-md no-underline hover:scale-105"
            >
              Request a Free Demo
            </Link>
          </div>
        </section>

        <section className="mb-16" data-aos="fade-up">
          <h4 className="text-2xl font-bold text-blue-600 text-center mb-12">FAQs</h4>
          <div className="space-y-4 max-w-3xl mx-auto">
            {[
              {
                question: 'What does school administration management software mean?',
                answer: 'It is a system that automates key school tasks like attendance, fee management, exams, and communication - all in one platform.',
              },
              {
                question: 'How does the School Administration Management Software help parents?',
                answer: 'Our software allows parents to track student attendance, view report cards, pay fees online, and request leave without visiting the school.',
              },
              {
                question: 'Can teachers manage their daily tasks using the software?',
                answer: 'Yes. Teachers can take attendance, upload assignments, update grades, and send messages to parents, all from one dashboard.',
              },
            ].map((faq, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl shadow-md overflow-hidden ${
                  visibleItems.includes(String(index + 15)) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                data-index={index + 15}
                ref={(el) => (itemRefs.current[index + 15] = el)}
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <button
                  className="w-full text-left p-6 bg-gray-50 hover:bg-gray-100 transition-colors duration-200 flex justify-between items-center"
                  onClick={() => toggleFaq(index)}
                >
                  <span className="text-lg font-semibold text-gray-900">{faq.question}</span>
                  <i className={`fas fa-chevron-${faqOpen[index] ? 'up' : 'down'} text-blue-600`}></i>
                </button>
                <div className={`p-6 text-gray-700 ${faqOpen[index] ? 'block' : 'hidden'}`}>
                  {faq.answer}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <Footer />
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 bg-blue-600 p-3 sm:p-4 rounded-full shadow-lg hover:bg-blue-700 transition hover:scale-110"
        aria-label="Back to top"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-5 sm:w-6 h-5 sm:h-6 text-white"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
        </svg>
      </button>
      <style jsx>{`
        .animate-fadeIn {
          animation: fadeIn 1s ease-out;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        /* Ensure images are fully responsive */
        img {
          max-width: 100%;
          height: auto;
        }
        @media (max-width: 640px) {
          .container {
            padding-left: 1rem;
            padding-right: 1rem;
          }
          h1 {
            font-size: 2.5rem;
          }
          h2 {
            font-size: 1.5rem;
          }
          .grid {
            grid-template-columns: 1fr;
          }
        }
        @media (min-width: 641px) and (max-width: 1024px) {
          .grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `}</style>
    </div>
  );
};

export default SchoolERP;